import React from "react";
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from "recharts";
import './VwapLineChart.css';

const getBucketKey = (dateStr, granularity) => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);

  if (granularity === "monthly") {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    return `${y}-${m}-01`;
  }
  if (granularity === "yearly") {
    return `${d.getUTCFullYear()}-01-01`;
  }
  return d.toISOString().slice(0, 10);
};

const bucketVwapForLongRanges = (data, selectedPeriod) => {
  if (!data || !data.length) return data;
  if (!["3Y", "5Y"].includes(selectedPeriod)) return data;

  const granularity = selectedPeriod === "3Y" ? "monthly" : "yearly";
  const sorted = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
  const buckets = new Map();

  sorted.forEach((item) => {
    const bucketKey = getBucketKey(item.date, granularity);
    if (!buckets.has(bucketKey)) {
      // Keep first trading day in bucket exactly as returned by API.
      buckets.set(bucketKey, { ...item });
    }
  });

  return Array.from(buckets.values());
};

const VWAPLineChart = ({ chartData, stockInfo, selectedPeriod }) => {
  if (!chartData || chartData.length === 0) return <p>Loading chart data...</p>;

  const plotData = bucketVwapForLongRanges(chartData, selectedPeriod);

  const xTicks =
    ['3Y', '5Y', 'MAX'].includes(selectedPeriod)
      ? Array.from(
          new Set(plotData.map(d => new Date(d.date).getFullYear()))
        )
          .map(year => {
            const first = plotData.find(d => new Date(d.date).getFullYear() === year);
            return first?.date;
          })
          .filter(Boolean)
      : undefined;

  return (
    <div className="chart-content">
      <h3>Volume Weighted Average Price Trend - {stockInfo.name} ({stockInfo.ticker})</h3>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsLineChart
            data={plotData}
            margin={{ top: 10, right: 10, left: 10, bottom: 6 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              interval="preserveStartEnd"
              tickFormatter={(value) => {
                const date = new Date(value);
                return ['3Y', '5Y', 'MAX'].includes(selectedPeriod)
                  ? date.getFullYear().toString()
                  : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              }}
              ticks={xTicks}
            />
            <YAxis
              domain={['auto', 'auto']}
              tick={{ fontSize: 12 }}
              width={60}
              tickFormatter={(value) => `$${value.toFixed(0)}`}
            />
            <Tooltip
              formatter={(value) => [`$${value.toFixed(2)}`, "VWAP"]}
              labelFormatter={(label) => `Date: ${label}`}
            />
            <Line
              type="monotone"
              dataKey="vwap"
              name="VWAP"
              stroke="#8884d8"
              strokeWidth={2}
              dot={{ r: 1 }}
              activeDot={{ r: 5 }}
            />
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default VWAPLineChart;
